'use client';

import React, { useMemo, useState } from 'react';

function currency(n: number): string {
  if (!isFinite(n)) return '—';
  return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
}

/**
 * Caravan loan repayment estimator. Standard amortisation with an optional
 * balloon (residual) — the balloon accrues interest for the full term and is
 * owed as a lump sum at the end, which is exactly why the total-cost line
 * matters more than the headline repayment.
 */
export default function LoanCalculator() {
  const [amount, setAmount] = useState(60000);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(5);
  const [balloonPct, setBalloonPct] = useState(0);

  const { monthly, totalInterest, totalPaid, balloon } = useMemo(() => {
    const r = rate / 100 / 12;
    const n = years * 12;
    const bal = amount * (balloonPct / 100);
    // PMT against the principal net of the balloon's present value.
    const financed = amount - bal / Math.pow(1 + r, n);
    const m = r === 0 ? financed / n : (financed * r) / (1 - Math.pow(1 + r, -n));
    const paid = m * n + bal;
    return { monthly: m, totalInterest: paid - amount, totalPaid: paid, balloon: bal };
  }, [amount, rate, years, balloonPct]);

  const row = 'flex items-baseline justify-between gap-4';
  const label = 'text-[13px] font-semibold uppercase tracking-wider text-[var(--ink-soft)]';

  return (
    <div className="rounded-2xl bg-[var(--sand-2)] cshadow p-7 sm:p-8">
      <div className="grid gap-8 lg:grid-cols-[1.2fr_1fr]">
        <div className="space-y-6">
          <div>
            <div className={row}>
              <span className={label}>Loan amount</span>
              <span className="font-heading nums text-lg text-[var(--ink)]">{currency(amount)}</span>
            </div>
            <input
              type="range" min={5000} max={250000} step={1000} value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="mt-2 w-full accent-[var(--blue)]" aria-label="Loan amount"
            />
          </div>
          <div>
            <div className={row}>
              <span className={label}>Interest rate (p.a.)</span>
              <span className="font-heading nums text-lg text-[var(--ink)]">{rate.toFixed(2)}%</span>
            </div>
            <input
              type="range" min={5} max={16} step={0.05} value={rate}
              onChange={(e) => setRate(Number(e.target.value))}
              className="mt-2 w-full accent-[var(--blue)]" aria-label="Interest rate"
            />
          </div>
          <div>
            <div className={row}>
              <span className={label}>Term</span>
              <span className="font-heading nums text-lg text-[var(--ink)]">{years} years</span>
            </div>
            <input
              type="range" min={1} max={7} step={1} value={years}
              onChange={(e) => setYears(Number(e.target.value))}
              className="mt-2 w-full accent-[var(--blue)]" aria-label="Loan term in years"
            />
          </div>
          <div>
            <div className={row}>
              <span className={label}>Balloon / residual</span>
              <span className="font-heading nums text-lg text-[var(--ink)]">
                {balloonPct}%{balloonPct > 0 ? ` (${currency(balloon)})` : ''}
              </span>
            </div>
            <input
              type="range" min={0} max={40} step={5} value={balloonPct}
              onChange={(e) => setBalloonPct(Number(e.target.value))}
              className="mt-2 w-full accent-[var(--blue)]" aria-label="Balloon percentage"
            />
          </div>
        </div>

        <div className="rounded-xl bg-[var(--sand)] p-6 flex flex-col justify-center">
          <div className="text-[13px] font-semibold uppercase tracking-wider text-[var(--ink-soft)]">
            Estimated repayment
          </div>
          <div className="font-heading nums mt-2 text-[2.6rem] leading-none text-[var(--ink)]">
            {currency(monthly)}<span className="text-lg text-[var(--ink-soft)]"> /month</span>
          </div>
          <dl className="mt-5 space-y-2 text-[14px]">
            <div className="flex justify-between">
              <dt className="text-[var(--ink-soft)]">Total interest</dt>
              <dd className="nums font-semibold text-[var(--ink)]">{currency(totalInterest)}</dd>
            </div>
            {balloon > 0 && (
              <div className="flex justify-between">
                <dt className="text-[var(--ink-soft)]">Balloon due at end</dt>
                <dd className="nums font-semibold text-[var(--ink)]">{currency(balloon)}</dd>
              </div>
            )}
            <div className="flex justify-between border-t border-[var(--hair)] pt-2">
              <dt className="text-[var(--ink-soft)]">Total cost of loan</dt>
              <dd className="nums font-semibold text-[var(--ink)]">{currency(totalPaid)}</dd>
            </div>
          </dl>
          <p className="mt-4 text-[11.5px] leading-relaxed text-[var(--ink-soft)]">
            Estimate only — repayments assume a fixed rate and monthly payments, and
            exclude fees. Your rate depends on your circumstances and the lender.
          </p>
        </div>
      </div>
    </div>
  );
}
